import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User, Radio, ArrowRight } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email, password } : { name, email, password };
      const { data } = await api.post(path, payload);
      setUser(data);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate("/feed");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/feed";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0A0D12] relative z-[2] flex flex-col lg:flex-row">
      {/* left brand panel */}
      <div className="lg:w-[46%] relative overflow-hidden border-b lg:border-b-0 lg:border-r border-white/10">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1623039405147-547794f92e9e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D12] via-[#0A0D12]/70 to-transparent" />
        <div className="relative z-10 p-8 sm:p-12 lg:p-16 h-full flex flex-col justify-between min-h-[320px]">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 grid place-items-center">
              <Radio className="w-6 h-6 text-[#06231a]" strokeWidth={2.5} />
            </div>
            <span className="font-serif text-2xl font-extrabold text-zinc-50">
              Pulse<span className="text-emerald-400">Forge</span>
            </span>
          </div>
          <div className="max-w-md">
            <p className="font-mono text-xs uppercase tracking-widest text-emerald-400 mb-4">
              X feed · your inputs · claude synthesis
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl font-extrabold leading-[1.05] text-zinc-50">
              Your feed, forged into fast, deep news.
            </h1>
            <p className="mt-5 text-zinc-400 leading-relaxed">
              Mash your X.com stream with your own topics and notes. Swipe crisp clips, then
              expand into deep briefings and export them into articles and books.
            </p>
          </div>
        </div>
      </div>

      {/* right form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <h2 className="font-serif text-3xl font-bold text-zinc-50">
            {mode === "login" ? "Sign in" : "Create account"}
          </h2>
          <p className="text-zinc-500 text-sm mt-1 mb-7">
            {mode === "login" ? "Pick up where your newsroom left off." : "Start building your personal newsroom."}
          </p>

          <button
            data-testid="login-google-button"
            onClick={googleLogin}
            className="w-full flex items-center justify-center gap-3 bg-white text-zinc-900 font-semibold rounded-xl py-3 hover:bg-zinc-200 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs font-mono text-zinc-600 uppercase tracking-widest">or</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  data-testid="register-name-input"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="pl-10 bg-[#121721] border-white/10 text-zinc-100 h-12 rounded-xl focus-visible:ring-emerald-500"
                />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                data-testid="login-email-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="pl-10 bg-[#121721] border-white/10 text-zinc-100 h-12 rounded-xl focus-visible:ring-emerald-500"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                data-testid="login-password-input"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="pl-10 bg-[#121721] border-white/10 text-zinc-100 h-12 rounded-xl focus-visible:ring-emerald-500"
              />
            </div>

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="w-full h-12 bg-emerald-500 hover:bg-emerald-400 text-[#06231a] font-semibold rounded-xl transition-colors active:scale-[0.98]"
            >
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <span className="flex items-center gap-2">{mode === "login" ? "Sign in" : "Create account"} <ArrowRight className="w-4 h-4" /></span>
              )}
            </Button>
          </form>

          <p className="text-sm text-zinc-500 mt-6 text-center">
            {mode === "login" ? "New to PulseForge?" : "Already have an account?"}{" "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-emerald-400 hover:text-emerald-300 font-medium transition-colors"
            >
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
